import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import {
  ThumbsUp,
  ThumbsDown,
  Minus,
  MessageSquare,
  Download,
  Search,
  X,
  ChevronDown,
  ChevronUp,
  FileText,
  Presentation,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import ReactWordcloud from "react-wordcloud";

const ANALYSIS_FIELDS = new Set([
  "embeddings", "Centroids", "processed_text", "Cluster Summary",
  "Cluster Phrase", "Cluster Theme Description", "Cluster",
  "Positive", "Neutral", "Negative", "Satisfied", "Neutral_Satisfaction",
  "Dissatisfied", "Sentiment", "Satisfaction",
]);

const ID_FIELDS = ["CustomerID", "GuestID", "RespondentID", "ID"];
const CLUSTER_COLORS = ["#6366f1", "#3b82f6", "#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444", "#ec4899"];
const RATING_COLORS  = ["#6366f1", "#3b82f6", "#8b5cf6", "#06b6d4", "#10b981"];

const SENTIMENT_COLORS = { Positive: "#22c55e", Neutral: "#f59e0b", Negative: "#ef4444" };
const SATISFACTION_COLORS = { Satisfied: "#22c55e", Neutral: "#f59e0b", Dissatisfied: "#ef4444" };


const WORDCLOUD_OPTIONS = {
  colors: ["#6366f1", "#3b82f6", "#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444"],
  enableTooltip: true,
  deterministic: true,
  fontFamily: "Inter, ui-sans-serif, sans-serif",
  fontSizes: [13, 52],
  fontStyle: "normal",
  fontWeight: "700",
  padding: 3,
  rotations: 1,
  rotationAngles: [0, 0],
  scale: "sqrt",
  spiral: "archimedean",
  transitionDuration: 0,
};

const TOOLTIP_STYLE = {
  contentStyle: { borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "11px", padding: "6px 10px" },
  itemStyle: { fontSize: "11px" },
  labelStyle: { fontWeight: 600, fontSize: "11px", marginBottom: "2px" },
};

// ── Shared card-title ──────────────────────────────────────────────────────────
const CardTitle = ({ children }) => (
  <div className="flex items-center gap-3 mb-3">
    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 whitespace-nowrap shrink-0">
      {children}
    </span>
    <div className="flex-1 h-px bg-gray-100" />
  </div>
);

const SentimentBadge = ({ sentiment }) => {
  if (sentiment === "Positive")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100">
        <ThumbsUp className="w-3 h-3" /> Positive
      </span>
    );
  if (sentiment === "Negative")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-100">
        <ThumbsDown className="w-3 h-3" /> Negative
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
      <Minus className="w-3 h-3" /> Neutral
    </span>
  );
};

const StarRating = ({ value, max = 5 }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: max }).map((_, i) => (
      <div key={i} className={`w-2 h-2 rounded-full ${i < value ? "bg-primary-blue" : "bg-gray-200"}`} />
    ))}
    <span className="ml-1 text-xs text-gray-500">{value}/{max}</span>
  </div>
);

// ── Sentiment donut (CSS, existing) ──────────────────────────────────────────
const SentimentDonut = ({ positive, neutral, negative }) => {
  const total = positive + neutral + negative || 1;
  const pos = Math.round((positive / total) * 100);
  const neu = Math.round((neutral / total) * 100);
  const posAngle = pos * 3.6;
  const neuAngle = (pos + neu) * 3.6;
  const dominant =
    pos >= neutral && pos >= (100 - pos - neu)
      ? { pct: pos, label: "positive", color: "text-green-600" }
      : (100 - pos - neu) >= pos && (100 - pos - neu) >= neu
      ? { pct: 100 - pos - neu, label: "negative", color: "text-red-500" }
      : { pct: neu, label: "neutral", color: "text-amber-500" };
  return (
    <div className="relative w-24 h-24 shrink-0">
      <div
        className="w-24 h-24 rounded-full"
        style={{ background: `conic-gradient(#22c55e 0deg ${posAngle}deg, #fbbf24 ${posAngle}deg ${neuAngle}deg, #ef4444 ${neuAngle}deg 360deg)` }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[60px] h-[60px] rounded-full bg-white shadow-inner flex flex-col items-center justify-center">
          <span className={`text-xs font-bold ${dominant.color}`}>{dominant.pct}%</span>
          <span className="text-[9px] text-gray-400 leading-tight">{dominant.label}</span>
        </div>
      </div>
    </div>
  );
};

// ── Custom pie label ──────────────────────────────────────────────────────────
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={600}>
      {`${Math.round(percent * 100)}%`}
    </text>
  );
};

class SurveyAnalysisSummary extends Component {
  static propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    fileName: PropTypes.string,
    wordFrequencies: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string, value: PropTypes.number })),
  };

  state = { filters: {}, page: 1, searchQ: "", showRawData: false, exporting: null };
  vizRef = createRef();

  // ── Column detection ──────────────────────────────────────────────────────
  detectIdColumn(data) {
    if (!data || !data[0]) return null;
    return ID_FIELDS.find((f) => f in data[0]) || null;
  }

  detectRatingColumns(data, idColumn) {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).filter((key) => {
      if (ANALYSIS_FIELDS.has(key)) return false;
      if (key === idColumn) return false;
      return (
        data.every((r) => {
          const v = r[key];
          if (v === null || v === undefined || v === "") return true;
          const n = Number(v);
          return !isNaN(n) && n >= 0 && n <= 10;
        }) &&
        data.some((r) => {
          const n = Number(r[key]);
          return !isNaN(n) && n > 0;
        })
      );
    });
  }

  detectOriginalColumns(data, ratingColumns, idColumn) {
    if (!data || data.length === 0) return [];
    const ratingSet = new Set(ratingColumns);
    return Object.keys(data[0]).filter(
      (key) => !ANALYSIS_FIELDS.has(key) && !ratingSet.has(key) && key !== idColumn
    );
  }

  // ── Summary stats ─────────────────────────────────────────────────────────
  computeSummaryStats(data) {
    const total = data.length;
    const positive    = data.filter((r) => r.Sentiment    === "Positive").length;
    const negative    = data.filter((r) => r.Sentiment    === "Negative").length;
    const neutral     = data.filter((r) => r.Sentiment    === "Neutral").length;
    const satisfied   = data.filter((r) => r.Satisfaction === "Satisfied").length;
    const dissatisfied= data.filter((r) => r.Satisfaction === "Dissatisfied").length;

    const idColumn      = this.detectIdColumn(data);
    const ratingColumns = this.detectRatingColumns(data, idColumn);
    const originalColumns = this.detectOriginalColumns(data, ratingColumns, idColumn);

    const avgRatings = {};
    ratingColumns.forEach((col) => {
      const sum = data.reduce((s, r) => s + (Number(r[col]) || 0), 0);
      avgRatings[col] = (sum / total).toFixed(1);
    });

    const clusterMap = {};
    data.forEach((r) => {
      const key = String(r.Cluster ?? "?");
      if (!clusterMap[key]) {
        clusterMap[key] = {
          phrase: r["Cluster Phrase"] || `Cluster ${key}`,
          description: r["Cluster Theme Description"] || "",
          count: 0, positive: 0, negative: 0, neutral: 0,
        };
      }
      clusterMap[key].count++;
      if      (r.Sentiment === "Positive") clusterMap[key].positive++;
      else if (r.Sentiment === "Negative") clusterMap[key].negative++;
      else                                  clusterMap[key].neutral++;
    });

    return {
      total, positive, negative, neutral, satisfied, dissatisfied,
      idColumn, ratingColumns, originalColumns, avgRatings, clusterMap,
    };
  }

  // ── Chart data helpers ────────────────────────────────────────────────────
  computeRatingDistribution(data, col) {
    const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    data.forEach((r) => {
      const v = Math.round(Number(r[col]));
      if (v >= 1 && v <= 5) dist[v]++;
    });
    return [1, 2, 3, 4, 5].map((s) => ({ score: String(s), count: dist[s] }));
  }

  computeAvgRatingsByTheme(data, ratingColumns) {
    const themeMap = {};
    data.forEach((r) => {
      const theme = r["Cluster Phrase"] || `Cluster ${r.Cluster}`;
      if (!themeMap[theme]) themeMap[theme] = { theme, sums: {}, counts: {} };
      ratingColumns.forEach((col) => {
        const v = Number(r[col]);
        if (!isNaN(v) && v > 0) {
          themeMap[theme].sums[col]   = (themeMap[theme].sums[col]   || 0) + v;
          themeMap[theme].counts[col] = (themeMap[theme].counts[col] || 0) + 1;
        }
      });
    });
    return Object.values(themeMap).map((t) => {
      const row = { theme: t.theme };
      ratingColumns.forEach((col) => {
        row[col] = t.counts[col] ? parseFloat((t.sums[col] / t.counts[col]).toFixed(1)) : 0;
      });
      return row;
    });
  }

  computeSentimentByTheme(data) {
    const map = {};
    data.forEach((r) => {
      const theme = r["Cluster Phrase"] || `Cluster ${r.Cluster}`;
      if (!map[theme]) map[theme] = { theme, Positive: 0, Neutral: 0, Negative: 0, total: 0 };
      map[theme].total++;
      if      (r.Sentiment === "Positive") map[theme].Positive++;
      else if (r.Sentiment === "Negative") map[theme].Negative++;
      else                                  map[theme].Neutral++;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }

  // ── Filter helpers ────────────────────────────────────────────────────────
  getUniqueValues(data, col) {
    const vals = new Set(
      data.map((r) => r[col]).filter((v) => v !== null && v !== undefined && v !== "" && v !== "None")
    );
    return [...vals].sort();
  }

  toggleFilter = (col, value) => {
    this.setState((prev) => {
      const current = prev.filters[col];
      return { filters: { ...prev.filters, [col]: current === value ? undefined : value }, page: 1 };
    });
  };

  clearFilter = (col) => {
    this.setState((prev) => ({ filters: { ...prev.filters, [col]: undefined }, page: 1 }));
  };

  clearFilters = () => this.setState({ filters: {}, page: 1, searchQ: "" });

  applyFilters(data, filters) {
    const { searchQ } = this.state;
    return data.filter((row) => {
      const matchesFilters = Object.entries(filters).every(([col, val]) => {
        if (!val) return true;
        return String(row[col]) === String(val);
      });
      if (!matchesFilters) return false;
      if (searchQ && searchQ.trim()) {
        const q = searchQ.toLowerCase().trim();
        return Object.values(row).some((v) => String(v || "").toLowerCase().includes(q));
      }
      return true;
    });
  }

  exportCSV = (data, stats) => {
    const idCol = stats.idColumn ? [stats.idColumn] : [];
    const cols = [...idCol, ...stats.originalColumns, ...stats.ratingColumns, "Sentiment", "Cluster Phrase"];
    const header = cols.map((c) => `"${c}"`).join(",");
    const rows = data.map((row) =>
      cols.map((col) => `"${String(row[col] ?? "").replace(/"/g, '""')}"`).join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "survey_results.csv";
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  exportPDF = async () => {
    const el = this.vizRef.current;
    if (!el) return;
    this.setState({ exporting: "pdf" });
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#f9fafb",
        ignoreElements: (node) => node.dataset && node.dataset.pdfExclude === "true",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const usableW = pageW - margin * 2;
      const imgH = (canvas.height * usableW) / canvas.width;
      const fileName = (this.props.fileName || "survey_analysis").replace(/\.[^.]+$/, "");

      // Add header on first page
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(30, 30, 60);
      pdf.text("Survey Analysis Report", margin, margin + 4);
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(120, 120, 140);
      pdf.text(fileName, margin, margin + 10);

      const startY = margin + 16;
      const firstPageImgH = pageH - startY - margin;

      if (imgH <= firstPageImgH) {
        pdf.addImage(imgData, "PNG", margin, startY, usableW, imgH);
      } else {
        const totalPx = canvas.height;
        const pxPerMm = totalPx / imgH;
        let yMm = 0;
        let firstPage = true;
        while (yMm < imgH) {
          if (!firstPage) pdf.addPage();
          const availH = firstPage ? firstPageImgH : pageH - margin * 2;
          const sliceH = Math.min(availH, imgH - yMm);
          const srcY = Math.round(yMm * pxPerMm);
          const srcH = Math.round(sliceH * pxPerMm);
          const slice = document.createElement("canvas");
          slice.width = canvas.width;
          slice.height = srcH;
          slice.getContext("2d").drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
          const sliceData = slice.toDataURL("image/png");
          const topY = firstPage ? startY : margin;
          pdf.addImage(sliceData, "PNG", margin, topY, usableW, sliceH);
          yMm += sliceH;
          firstPage = false;
        }
      }
      pdf.save(`${fileName}_analysis.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
    } finally {
      this.setState({ exporting: null });
    }
  };

  exportPPTX = async (stats) => {
    this.setState({ exporting: "pptx" });
    try {
      const { default: pptxgen } = await import("pptxgenjs");
      const { data, fileName: fName, wordFrequencies } = this.props;
      const filteredData = this.applyFilters(data, this.state.filters);
      const fileName = (fName || "survey_analysis").replace(/\.[^.]+$/, "");

      const pptx = new pptxgen();
      pptx.layout = "LAYOUT_WIDE";
      pptx.title = "Survey Analysis";

      const BRAND = "#4f46e5";
      const DARK  = "#1e1b4b";
      const GREEN = "#22c55e";
      const AMBER = "#f59e0b";
      const RED   = "#ef4444";

      const totalR = stats.total || 1;
      const pctPos = Math.round((stats.positive / totalR) * 100);
      const pctNeg = Math.round((stats.negative / totalR) * 100);
      const pctNeu = Math.round((stats.neutral  / totalR) * 100);
      const sentScore = pctPos - pctNeg;

      // ── Slide 1: Title ─────────────────────────────────────────────────────
      const s1 = pptx.addSlide();
      s1.background = { fill: DARK };
      s1.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: "100%", h: 1.2, fill: { color: BRAND } });
      s1.addText("Survey Analysis Report", { x: 0.4, y: 0.2, w: "90%", h: 0.8, fontSize: 32, bold: true, color: "FFFFFF", fontFace: "Calibri" });
      s1.addText(fileName, { x: 0.4, y: 1.5, w: "90%", h: 0.4, fontSize: 16, color: "A5B4FC", fontFace: "Calibri" });
      s1.addText(`${stats.total.toLocaleString()} responses analysed`, { x: 0.4, y: 2.0, w: "90%", h: 0.35, fontSize: 13, color: "C7D2FE", fontFace: "Calibri" });

      // KPI grid
      const kpis = [
        { label: "Total", val: stats.total.toLocaleString(), color: "60A5FA" },
        { label: "Positive", val: `${pctPos}%`, color: "4ADE80" },
        { label: "Negative", val: `${pctNeg}%`, color: "F87171" },
        { label: "Neutral",  val: `${pctNeu}%`, color: "FCD34D" },
        { label: "Score",    val: `${sentScore >= 0 ? "+" : ""}${sentScore}`, color: sentScore >= 0 ? "4ADE80" : "F87171" },
        { label: "Themes",   val: String(Object.keys(stats.clusterMap).length), color: "A78BFA" },
      ];
      kpis.forEach((k, i) => {
        const x = 0.4 + (i % 3) * 4.3;
        const y = 2.7 + Math.floor(i / 3) * 1.3;
        s1.addShape(pptx.ShapeType.roundRect, { x, y, w: 3.9, h: 1.1, fill: { color: "1E1A3A" }, line: { color: k.color, width: 2 }, rectRadius: 0.08 });
        s1.addText(k.val, { x, y: y + 0.05, w: 3.9, h: 0.65, fontSize: 22, bold: true, color: k.color, align: "center", fontFace: "Calibri" });
        s1.addText(k.label, { x, y: y + 0.65, w: 3.9, h: 0.35, fontSize: 11, color: "9CA3AF", align: "center", fontFace: "Calibri" });
      });

      // ── Slide 2: Sentiment breakdown ───────────────────────────────────────
      const s2 = pptx.addSlide();
      s2.background = { fill: "F9FAFB" };
      s2.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: "100%", h: 0.7, fill: { color: BRAND } });
      s2.addText("Sentiment & Satisfaction", { x: 0.4, y: 0.1, w: "90%", h: 0.5, fontSize: 20, bold: true, color: "FFFFFF", fontFace: "Calibri" });

      // Sentiment bar chart data
      const sentData = [
        { name: "Positive", labels: [{ data: pctPos }], color: GREEN.replace("#","") },
        { name: "Neutral",  labels: [{ data: pctNeu }], color: AMBER.replace("#","") },
        { name: "Negative", labels: [{ data: pctNeg }], color: RED.replace("#","") },
      ];
      s2.addChart(pptx.ChartType.bar, sentData, {
        x: 0.4, y: 0.9, w: 6.5, h: 4.0,
        barDir: "bar",
        chartColors: [GREEN.replace("#",""), AMBER.replace("#",""), RED.replace("#","")],
        showLegend: true, legendPos: "b",
        dataLabelFormatCode: '0"%"',
        showValue: true,
        valAxisMaxVal: 100,
        catAxisLabelFontSize: 11,
        title: "Sentiment % Distribution",
        showTitle: false,
      });

      // Satisfaction summary text box
      const neutralSat = stats.total - stats.satisfied - stats.dissatisfied;
      const pctSat = Math.round((stats.satisfied / totalR) * 100);
      const pctDis = Math.round((stats.dissatisfied / totalR) * 100);
      s2.addShape(pptx.ShapeType.roundRect, { x: 7.2, y: 0.9, w: 5.5, h: 4.0, fill: { color: "FFFFFF" }, line: { color: "E5E7EB" }, rectRadius: 0.1 });
      s2.addText("Satisfaction", { x: 7.4, y: 1.0, w: 5.1, h: 0.4, fontSize: 13, bold: true, color: DARK.replace("#",""), fontFace: "Calibri" });
      [
        { label: "Satisfied",    val: `${pctSat}%  (${stats.satisfied})`,    color: GREEN.replace("#","") },
        { label: "Neutral",      val: `${Math.round(neutralSat/totalR*100)}%  (${neutralSat})`, color: AMBER.replace("#","") },
        { label: "Dissatisfied", val: `${pctDis}%  (${stats.dissatisfied})`, color: RED.replace("#","") },
      ].forEach((item, i) => {
        s2.addText(item.label, { x: 7.4, y: 1.55 + i * 0.85, w: 2.8, h: 0.4, fontSize: 12, color: "6B7280", fontFace: "Calibri" });
        s2.addText(item.val,   { x: 10.0, y: 1.55 + i * 0.85, w: 2.5, h: 0.4, fontSize: 12, bold: true, color: item.color, align: "right", fontFace: "Calibri" });
      });

      // ── Slide 3: Themes ────────────────────────────────────────────────────
      const s3 = pptx.addSlide();
      s3.background = { fill: "F9FAFB" };
      s3.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: "100%", h: 0.7, fill: { color: BRAND } });
      s3.addText("Themes & Sentiment by Theme", { x: 0.4, y: 0.1, w: "90%", h: 0.5, fontSize: 20, bold: true, color: "FFFFFF", fontFace: "Calibri" });

      const clusters = Object.values(stats.clusterMap).sort((a, b) => b.count - a.count);
      const themeRows = clusters.map((c, i) => ({
        Rank: { text: String(i + 1), options: { bold: true, color: BRAND.replace("#","") } },
        Theme: { text: c.phrase, options: { color: DARK.replace("#","") } },
        Count: { text: String(c.count), options: { bold: true } },
        Positive: { text: `${c.count ? Math.round(c.positive / c.count * 100) : 0}%`, options: { color: GREEN.replace("#",""), bold: true } },
        Neutral:  { text: `${c.count ? Math.round(c.neutral  / c.count * 100) : 0}%`, options: { color: AMBER.replace("#","") } },
        Negative: { text: `${c.count ? Math.round(c.negative / c.count * 100) : 0}%`, options: { color: RED.replace("#","") } },
      }));

      s3.addTable(
        [
          [
            { text: "#",        options: { bold: true, fill: BRAND.replace("#",""), color: "FFFFFF", fontSize: 11 } },
            { text: "Theme",    options: { bold: true, fill: BRAND.replace("#",""), color: "FFFFFF", fontSize: 11 } },
            { text: "Count",    options: { bold: true, fill: BRAND.replace("#",""), color: "FFFFFF", fontSize: 11, align: "center" } },
            { text: "Positive", options: { bold: true, fill: BRAND.replace("#",""), color: "FFFFFF", fontSize: 11, align: "center" } },
            { text: "Neutral",  options: { bold: true, fill: BRAND.replace("#",""), color: "FFFFFF", fontSize: 11, align: "center" } },
            { text: "Negative", options: { bold: true, fill: BRAND.replace("#",""), color: "FFFFFF", fontSize: 11, align: "center" } },
          ],
          ...themeRows.map((r) => [r.Rank, r.Theme, r.Count, r.Positive, r.Neutral, r.Negative]),
        ],
        {
          x: 0.4, y: 0.85, w: 12.4, h: 4.5,
          fontSize: 11, border: { type: "solid", color: "E5E7EB" },
          rowH: 0.42,
          colW: [0.5, 5.5, 1.0, 1.3, 1.3, 1.3],
        }
      );

      // ── Slide 4: Sentiment by theme chart ──────────────────────────────────
      const sentByTheme = this.computeSentimentByTheme(filteredData);
      if (sentByTheme.length > 0) {
        const s4 = pptx.addSlide();
        s4.background = { fill: "F9FAFB" };
        s4.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: "100%", h: 0.7, fill: { color: BRAND } });
        s4.addText("Sentiment by Theme", { x: 0.4, y: 0.1, w: "90%", h: 0.5, fontSize: 20, bold: true, color: "FFFFFF", fontFace: "Calibri" });

        const chartData4 = [
          { name: "Positive", labels: sentByTheme.map((r) => ({ data: r.Positive })), color: GREEN.replace("#","") },
          { name: "Neutral",  labels: sentByTheme.map((r) => ({ data: r.Neutral  })), color: AMBER.replace("#","") },
          { name: "Negative", labels: sentByTheme.map((r) => ({ data: r.Negative })), color: RED.replace("#","") },
        ];
        s4.addChart(pptx.ChartType.bar, chartData4, {
          x: 0.4, y: 0.85, w: 12.4, h: 4.5,
          barDir: "col",
          barGrouping: "stacked",
          chartColors: [GREEN.replace("#",""), AMBER.replace("#",""), RED.replace("#","")],
          showLegend: true, legendPos: "b",
          catAxisLabelFontSize: 10,
          catAxisLabelPos: "low",
          showValue: false,
        });
      }

      // ── Slide 5: Top keywords ──────────────────────────────────────────────
      const words = wordFrequencies || [];
      if (words.length > 0) {
        const s5 = pptx.addSlide();
        s5.background = { fill: "F9FAFB" };
        s5.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: "100%", h: 0.7, fill: { color: BRAND } });
        s5.addText("Top Keywords", { x: 0.4, y: 0.1, w: "90%", h: 0.5, fontSize: 20, bold: true, color: "FFFFFF", fontFace: "Calibri" });

        const top30 = words.slice(0, 30);
        s5.addChart(pptx.ChartType.bar, [{ name: "Frequency", labels: top30.map((w) => ({ data: w.value })) }], {
          x: 0.4, y: 0.85, w: 12.4, h: 4.5,
          barDir: "bar",
          catAxisLabels: top30.map((w) => w.text),
          chartColors: [BRAND.replace("#","")],
          showLegend: false,
          showValue: true,
          catAxisLabelFontSize: 10,
          dataLabelFontSize: 9,
          valAxisMaxVal: Math.max(...top30.map((w) => w.value)) + 2,
        });
      }

      // ── Slide 6: Ratings (if any) ──────────────────────────────────────────
      if (stats.ratingColumns.length > 0) {
        const s6 = pptx.addSlide();
        s6.background = { fill: "F9FAFB" };
        s6.addShape(pptx.ShapeType.rect, { x: 0, y: 0, w: "100%", h: 0.7, fill: { color: BRAND } });
        s6.addText("Average Ratings by Theme", { x: 0.4, y: 0.1, w: "90%", h: 0.5, fontSize: 20, bold: true, color: "FFFFFF", fontFace: "Calibri" });

        const avgByTheme = this.computeAvgRatingsByTheme(filteredData, stats.ratingColumns);
        const ratingChartColors = ["6366F1", "3B82F6", "8B5CF6", "06B6D4", "10B981"];
        const ratingChartData = stats.ratingColumns.map((col, i) => ({
          name: col,
          labels: avgByTheme.map((t) => ({ data: t[col] || 0 })),
          color: ratingChartColors[i % ratingChartColors.length],
        }));
        s6.addChart(pptx.ChartType.bar, ratingChartData, {
          x: 0.4, y: 0.85, w: 12.4, h: 4.5,
          barDir: "col",
          barGrouping: "clustered",
          catAxisLabels: avgByTheme.map((t) => t.theme.length > 15 ? t.theme.slice(0, 15) + "…" : t.theme),
          chartColors: ratingChartColors,
          showLegend: true, legendPos: "b",
          showValue: true,
          dataLabelFontSize: 9,
          valAxisMaxVal: 5,
          catAxisLabelFontSize: 10,
        });
      }

      await pptx.writeFile({ fileName: `${fileName}_analysis.pptx` });
    } catch (err) {
      console.error("PPTX export failed:", err);
    } finally {
      this.setState({ exporting: null });
    }
  };

  // ── Render: filter bar ────────────────────────────────────────────────────
  renderFilterBar(data, stats, filteredData) {
    const { filters, searchQ } = this.state;
    const sentimentVals = this.getUniqueValues(data, "Sentiment");
    const clusterVals   = this.getUniqueValues(data, "Cluster Phrase");
    const activeFilterCount = Object.values(filters).filter(Boolean).length + (searchQ ? 1 : 0);
    const pillBase = "px-2.5 py-1 rounded-full text-xs font-medium border transition-all";
    const pillOff  = "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700";
    const pillAllOn = "bg-gray-800 text-white border-gray-800";

    return (
      <div className="flex items-center gap-2 flex-wrap py-2.5 px-4 rounded-xl bg-gray-50 border border-gray-100">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-300 shrink-0">Sentiment</span>
        <button onClick={() => this.clearFilter("Sentiment")} className={`${pillBase} ${!filters.Sentiment ? pillAllOn : pillOff}`}>All</button>
        {sentimentVals.map((val) => {
          const active = filters.Sentiment === val;
          const color = SENTIMENT_COLORS[val] || "#6366f1";
          return (
            <button key={val} onClick={() => this.toggleFilter("Sentiment", val)}
              className={`${pillBase} ${active ? "text-white border-transparent" : pillOff}`}
              style={active ? { background: color, borderColor: color } : {}}>
              {val}
            </button>
          );
        })}

        <div className="w-px h-4 bg-gray-200 mx-0.5 shrink-0" />

        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-300 shrink-0">Theme</span>
        <button onClick={() => this.clearFilter("Cluster Phrase")} className={`${pillBase} ${!filters["Cluster Phrase"] ? pillAllOn : pillOff}`}>All</button>
        {clusterVals.map((val) => {
          const active = filters["Cluster Phrase"] === val;
          return (
            <button key={val} onClick={() => this.toggleFilter("Cluster Phrase", val)}
              className={`${pillBase} ${active ? "bg-indigo-600 text-white border-indigo-600" : pillOff}`}>
              {val}
            </button>
          );
        })}

        <div className="ml-auto flex items-center gap-2 shrink-0">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
            <input
              type="text" placeholder="Search…" value={searchQ}
              onChange={(e) => this.setState({ searchQ: e.target.value, page: 1 })}
              className="pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:border-blue-300 w-36"
            />
          </div>
          {activeFilterCount > 0 && (
            <button onClick={this.clearFilters} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 font-medium">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
          <span className="text-xs text-gray-400 whitespace-nowrap">
            <span className="font-semibold text-gray-700">{filteredData.length}</span>
            <span className="text-gray-300"> / {stats.total}</span>
          </span>
        </div>
      </div>
    );
  }

  // ── Render: KPI cards ─────────────────────────────────────────────────────
  renderKPICards(stats) {
    const pctPositive = Math.round((stats.positive / stats.total) * 100);
    const pctNegative = Math.round((stats.negative / stats.total) * 100);
    const pctNeutral  = Math.round((stats.neutral  / stats.total) * 100);
    const sentimentScore = pctPositive - pctNegative;
    const numThemes  = Object.keys(stats.clusterMap).length;
    const topTheme   = Object.values(stats.clusterMap).sort((a, b) => b.count - a.count)[0];

    const cards = [
      { label: "Total Responses",   value: stats.total.toLocaleString(), color: "#3b82f6", sub: "survey respondents" },
      { label: "Positive Sentiment",value: `${pctPositive}%`,   color: "#22c55e", sub: `${stats.positive} responses` },
      { label: "Negative Sentiment",value: `${pctNegative}%`,   color: "#ef4444", sub: `${stats.negative} responses` },
      { label: "Neutral Sentiment", value: `${pctNeutral}%`,    color: "#f59e0b", sub: `${stats.neutral} responses` },
      { label: "Sentiment Score",   value: `${sentimentScore >= 0 ? "+" : ""}${sentimentScore}`, color: sentimentScore >= 0 ? "#22c55e" : "#ef4444", sub: "positive − negative" },
      { label: "Themes Found",      value: String(numThemes),    color: "#6366f1", sub: topTheme ? topTheme.phrase : "clusters" },
    ];

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map((card) => (
          <div key={card.label} className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex flex-col gap-1 shadow-sm overflow-hidden" style={{ borderTop: `2px solid ${card.color}` }}>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-tight">{card.label}</span>
            <span className="text-2xl font-bold leading-none" style={{ color: card.color }}>{card.value}</span>
            <span className="text-[11px] text-gray-400 truncate">{card.sub}</span>
          </div>
        ))}
      </div>
    );
  }

  // ── Render: Sentiment distribution (CSS donut, existing) ──────────────────
  renderSentimentDonut(stats, activeFilterCount = 0) {
    const total = stats.total || 1;
    const rows = [
      { label: "Positive", count: stats.positive, color: "#22c55e" },
      { label: "Neutral",  count: stats.neutral,  color: "#f59e0b" },
      { label: "Negative", count: stats.negative, color: "#ef4444" },
    ];
    return (
      <div className="rounded-xl border border-gray-100 bg-white px-4 py-4 shadow-sm">
        <CardTitle>Sentiment Distribution{activeFilterCount > 0 ? " · filtered" : ""}</CardTitle>
        <div className="flex items-center gap-5">
          <SentimentDonut positive={stats.positive} neutral={stats.neutral} negative={stats.negative} />
          <div className="flex flex-col gap-2.5 flex-1">
            {rows.map((item) => {
              const pct = Math.round((item.count / total) * 100);
              return (
                <div key={item.label} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background: item.color }} />
                  <span className="text-xs text-gray-500 w-14">{item.label}</span>
                  <span className="text-xs font-bold w-7" style={{ color: item.color }}>{item.count}</span>
                  <span className="text-[10px] text-gray-400">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Satisfaction breakdown (Recharts PieChart) ────────────────────
  renderSatisfactionChart(stats, activeFilterCount = 0) {
    const neutralSat = stats.total - stats.satisfied - stats.dissatisfied;
    const data = [
      { name: "Satisfied",    value: stats.satisfied,    color: SATISFACTION_COLORS.Satisfied },
      { name: "Neutral",      value: neutralSat,          color: SATISFACTION_COLORS.Neutral },
      { name: "Dissatisfied", value: stats.dissatisfied, color: SATISFACTION_COLORS.Dissatisfied },
    ].filter((d) => d.value > 0);

    return (
      <div className="rounded-xl border border-gray-100 bg-white px-4 py-4 shadow-sm">
        <CardTitle>Satisfaction Breakdown{activeFilterCount > 0 ? " · filtered" : ""}</CardTitle>
        <ResponsiveContainer width="100%" height={160}>
          <PieChart>
            <Pie
              data={data} cx="50%" cy="50%"
              innerRadius={42} outerRadius={68}
              dataKey="value" paddingAngle={2}
              labelLine={false} label={renderPieLabel}
            >
              {data.map((d, i) => <Cell key={i} fill={d.color} stroke="white" strokeWidth={2} />)}
            </Pie>
            <Tooltip {...TOOLTIP_STYLE} formatter={(val, name) => [`${val} (${Math.round(val / stats.total * 100)}%)`, name]} />
            <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: "11px" }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // ── Render: Theme card (CSS bars, existing style) ─────────────────────────
  renderThemeCard(stats, activeFilterCount = 0) {
    const clusters = Object.values(stats.clusterMap).sort((a, b) => b.count - a.count);
    const maxCount = Math.max(...clusters.map((c) => c.count), 1);
    return (
      <div className="rounded-xl border border-gray-100 bg-white px-4 py-4 shadow-sm flex flex-col">
        <CardTitle>Themes · {clusters.length} cluster{clusters.length !== 1 ? "s" : ""}{activeFilterCount > 0 ? " · filtered" : ""}</CardTitle>
        <div className="flex flex-col flex-1">
          {clusters.map((cluster, i) => {
            const posPct = cluster.count ? Math.round((cluster.positive / cluster.count) * 100) : 0;
            const neuPct = cluster.count ? Math.round((cluster.neutral  / cluster.count) * 100) : 0;
            const negPct = 100 - posPct - neuPct;
            const barWidth = Math.round((cluster.count / maxCount) * 100);
            const color = CLUSTER_COLORS[i % CLUSTER_COLORS.length];
            return (
              <div key={cluster.phrase} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
                <div className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: `${color}18`, color }}>{i + 1}</div>
                <span className="text-xs font-medium text-gray-700 w-32 shrink-0 truncate" title={cluster.phrase}>{cluster.phrase}</span>
                <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full flex rounded-full overflow-hidden" style={{ width: `${barWidth}%` }}>
                      <div className="h-full bg-green-400" style={{ width: `${posPct}%` }} />
                      <div className="h-full bg-amber-300"  style={{ width: `${neuPct}%` }} />
                      <div className="h-full bg-red-400"    style={{ width: `${negPct}%` }} />
                    </div>
                  </div>
                  <div className="flex gap-2 text-[9px]">
                    <span className="text-green-600 font-medium">{cluster.positive}↑</span>
                    <span className="text-amber-500">{cluster.neutral}–</span>
                    <span className="text-red-500 font-medium">{cluster.negative}↓</span>
                  </div>
                </div>
                <span className="text-base font-bold w-8 text-right shrink-0" style={{ color }}>{cluster.count}</span>
              </div>
            );
          })}
        </div>
        <div className="flex gap-3 text-[10px] text-gray-400 pt-2 mt-auto border-t border-gray-50">
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />Positive</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-300 inline-block" />Neutral</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />Negative</span>
        </div>
      </div>
    );
  }

  // ── Render: Sentiment by theme stacked bar ────────────────────────────────
  renderSentimentByThemeBar(filteredData, activeFilterCount = 0) {
    const chartData = this.computeSentimentByTheme(filteredData);
    const truncate = (s, n = 14) => s.length > n ? s.slice(0, n) + "…" : s;

    return (
      <div className="rounded-xl border border-gray-100 bg-white px-4 py-4 shadow-sm">
        <CardTitle>Sentiment by Theme{activeFilterCount > 0 ? " · filtered" : ""}</CardTitle>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 48 }} barSize={28}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="theme"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              tickFormatter={(v) => truncate(v)}
              angle={-30} textAnchor="end" interval={0}
            />
            <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} />
            <Tooltip {...TOOLTIP_STYLE} />
            <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />
            <Bar dataKey="Positive" stackId="s" fill={SENTIMENT_COLORS.Positive} radius={[0, 0, 0, 0]} />
            <Bar dataKey="Neutral"  stackId="s" fill={SENTIMENT_COLORS.Neutral}  radius={[0, 0, 0, 0]} />
            <Bar dataKey="Negative" stackId="s" fill={SENTIMENT_COLORS.Negative} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // ── Render: Rating score distribution grid ────────────────────────────────
  renderRatingDistributionGrid(filteredData, ratingColumns) {
    const cols = Math.min(ratingColumns.length, 3);
    return (
      <div className="rounded-xl border border-gray-100 bg-white px-4 py-4 shadow-sm">
        <CardTitle>Rating Score Distribution · {ratingColumns.length} dimension{ratingColumns.length !== 1 ? "s" : ""}</CardTitle>
        <div className={`grid gap-4`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {ratingColumns.map((col) => {
            const distData = this.computeRatingDistribution(filteredData, col);
            const maxCount = Math.max(...distData.map((d) => d.count), 1);
            return (
              <div key={col}>
                <p className="text-[11px] font-semibold text-gray-500 mb-2 truncate" title={col}>{col}</p>
                <ResponsiveContainer width="100%" height={90}>
                  <BarChart data={distData} margin={{ top: 0, right: 0, left: -30, bottom: 0 }} barSize={16}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="score" tick={{ fontSize: 9, fill: "#9ca3af" }} />
                    <YAxis tick={{ fontSize: 9, fill: "#9ca3af" }} domain={[0, maxCount + 1]} allowDecimals={false} />
                    <Tooltip {...TOOLTIP_STYLE} labelFormatter={(l) => `Score ${l}`} formatter={(v) => [v, "Responses"]} />
                    <Bar dataKey="count" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Render: Average ratings by theme grouped bar ──────────────────────────
  renderAvgRatingsByTheme(filteredData, ratingColumns) {
    const chartData = this.computeAvgRatingsByTheme(filteredData, ratingColumns);
    const truncate = (s, n = 14) => s.length > n ? s.slice(0, n) + "…" : s;

    return (
      <div className="rounded-xl border border-gray-100 bg-white px-4 py-4 shadow-sm">
        <CardTitle>Average Ratings by Theme</CardTitle>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 48 }} barGap={2} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="theme"
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              tickFormatter={(v) => truncate(v)}
              angle={-30} textAnchor="end" interval={0}
            />
            <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: "#9ca3af" }} />
            <Tooltip {...TOOLTIP_STYLE} formatter={(v, name) => [`${v} / 5`, name]} />
            <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />
            {ratingColumns.map((col, i) => (
              <Bar key={col} dataKey={col} fill={RATING_COLORS[i % RATING_COLORS.length]} radius={[3, 3, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // ── Render: Raw data table ────────────────────────────────────────────────
  renderRawDataTable(filteredData, pagedData, stats, page, pageCount) {
    const thClass = "text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap";
    return (
      <div className="flex flex-col gap-2">
        <div className="overflow-x-auto rounded-xl border border-gray-100 shadow-sm">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                <th className={thClass}>#</th>
                {stats.originalColumns.map((col) => <th key={col} className={thClass}>{col}</th>)}
                <th className={thClass}>Sentiment</th>
                {stats.ratingColumns.length > 0 && <th className={thClass}>Ratings</th>}
                <th className={thClass}>Theme</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={stats.originalColumns.length + 3 + (stats.ratingColumns.length > 0 ? 1 : 0)} className="px-4 py-8 text-center text-xs text-gray-400">
                    No responses match the selected filters.
                  </td>
                </tr>
              ) : (
                pagedData.map((row, idx) => {
                  const rowId  = stats.idColumn ? row[stats.idColumn] : null;
                  const rowNum = (page - 1) * 50 + idx + 1;
                  return (
                    <tr key={idx} className={`border-b border-gray-100 transition-colors hover:bg-blue-50/30 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap font-mono">{rowId ?? rowNum}</td>
                      {stats.originalColumns.map((col) => {
                        const val = row[col];
                        const isEmpty = val === null || val === undefined || val === "" || val === "None";
                        return (
                          <td key={col} className="px-4 py-3 text-xs text-gray-700 max-w-xs">
                            {isEmpty ? <span className="text-gray-300">—</span> : <span className="line-clamp-2">{val}</span>}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 whitespace-nowrap"><SentimentBadge sentiment={row.Sentiment} /></td>
                      {stats.ratingColumns.length > 0 && (
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            {stats.ratingColumns.map((col) => (
                              <div key={col} className="flex items-center gap-1">
                                <span className="text-xs text-gray-400 w-8 truncate" title={col}>
                                  {col.replace(/([A-Z])/g, " $1").trim().split(" ").map((w) => w[0]).join("").slice(0, 3)}
                                </span>
                                <StarRating value={Number(row[col]) || 0} />
                              </div>
                            ))}
                          </div>
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 whitespace-nowrap">
                          {row["Cluster Phrase"] || `Cluster ${row.Cluster}`}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {pageCount > 1 && (
          <div className="flex items-center justify-between px-2 py-2">
            <span className="text-xs text-gray-400">
              Showing {(page - 1) * 50 + 1}–{Math.min(page * 50, filteredData.length)} of {filteredData.length}
            </span>
            <div className="flex items-center gap-1">
              <button disabled={page === 1} onClick={() => this.setState((s) => ({ page: s.page - 1 }))}
                className="px-3 py-1 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-100 transition-colors">←</button>
              <span className="text-xs text-gray-500 px-2">{page} / {pageCount}</span>
              <button disabled={page >= pageCount} onClick={() => this.setState((s) => ({ page: s.page + 1 }))}
                className="px-3 py-1 text-xs rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-100 transition-colors">→</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderWordCloud() {
    const words = this.props.wordFrequencies || [];
    if (words.length === 0) return null;

    return (
      <div className="rounded-xl border border-gray-100 bg-white px-4 py-4 shadow-sm">
        <CardTitle>Word Cloud · top {words.length} terms · all responses</CardTitle>
        <div style={{ height: 260 }}>
          <ReactWordcloud words={words} options={WORDCLOUD_OPTIONS} />
        </div>
      </div>
    );
  }

  render() {
    const { data } = this.props;
    if (!data || data.length === 0)
      return <div className="text-gray-400 text-sm">No survey data available.</div>;

    const { filters, page, searchQ, showRawData } = this.state;
    const pageSize    = 50;
    const stats       = this.computeSummaryStats(data);
    const filteredData= this.applyFilters(data, filters);
    const activeFilterCount = Object.values(filters).filter(Boolean).length + (searchQ ? 1 : 0);
    const pageCount   = Math.ceil(filteredData.length / pageSize);
    const pagedData   = filteredData.slice((page - 1) * pageSize, page * pageSize);
    const visStats    = activeFilterCount > 0
      ? this.computeSummaryStats(filteredData.length > 0 ? filteredData : data)
      : stats;

    const hasRatings  = stats.ratingColumns.length > 0;
    const hasThemes   = Object.keys(stats.clusterMap).length > 1;

    const { exporting } = this.state;

    return (
      <div className="flex flex-col gap-5 font-inter">

        {/* ── Export toolbar ── */}
        <div className="flex items-center justify-end gap-2" data-pdf-exclude="true">
          <button
            onClick={this.exportPDF}
            disabled={!!exporting}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-500 hover:border-rose-300 hover:text-rose-600 transition-all disabled:opacity-40"
          >
            <FileText className="w-3.5 h-3.5" />
            {exporting === "pdf" ? "Generating…" : "Export PDF"}
          </button>
          <button
            onClick={() => this.exportPPTX(stats)}
            disabled={!!exporting}
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-500 hover:border-orange-300 hover:text-orange-600 transition-all disabled:opacity-40"
          >
            <Presentation className="w-3.5 h-3.5" />
            {exporting === "pptx" ? "Generating…" : "Export PowerPoint"}
          </button>
        </div>

        {/* ── Visualization container (captured for PDF) ── */}
        <div ref={this.vizRef} className="flex flex-col gap-5">

        {/* ── Filter bar ── */}
        {this.renderFilterBar(data, stats, filteredData)}

        {/* ── KPI cards ── */}
        {this.renderKPICards(visStats)}

        {/* ── Row 1: Sentiment donut + Satisfaction pie + Theme overview ── */}
        <div className={`grid grid-cols-1 gap-4 ${hasRatings || hasThemes ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
          {this.renderSentimentDonut(visStats, activeFilterCount)}
          {this.renderSatisfactionChart(visStats, activeFilterCount)}
          {(hasThemes) && this.renderThemeCard(visStats, activeFilterCount)}
        </div>

        {/* ── Row 2: Sentiment by theme stacked bar + Word cloud ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {hasThemes && this.renderSentimentByThemeBar(filteredData, activeFilterCount)}
          {this.renderWordCloud()}
        </div>

        {/* ── Row 3: Rating score distribution ── */}
        {hasRatings && this.renderRatingDistributionGrid(filteredData, stats.ratingColumns)}

        {/* ── Row 4: Average ratings by theme ── */}
        {hasRatings && hasThemes && this.renderAvgRatingsByTheme(filteredData, stats.ratingColumns)}

        </div>{/* end vizRef */}

        {/* ── Raw data toggle ── */}
        <div className="flex flex-col gap-3" data-pdf-exclude="true">
          <div className="flex items-center justify-between">
            <button
              onClick={() => this.setState((s) => ({ showRawData: !s.showRawData }))}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-xs font-semibold text-gray-500 hover:border-gray-300 hover:text-gray-700 transition-all shadow-sm"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              {showRawData ? "Hide" : "View"} Raw Responses
              <span className="text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full font-normal">{filteredData.length}</span>
              {showRawData ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showRawData && (
              <button
                onClick={() => this.exportCSV(filteredData, stats)}
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-500 hover:border-green-300 hover:text-green-600 transition-all"
              >
                <Download className="w-3 h-3" /> Export CSV
              </button>
            )}
          </div>

          {showRawData && this.renderRawDataTable(filteredData, pagedData, stats, page, pageCount)}
        </div>

      </div>
    );
  }
}

export default SurveyAnalysisSummary;
